import React, { useEffect, useReducer } from "react";
import {Button, IconButton, Modal} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { NotificationManager } from "react-notifications";
import { client } from "../../config/apollo";
import { ADD_USER_PAYMENT } from "../../graphql/mutations/user";
import { initReducer } from "../../utils/common-methods";
import {Cancel} from "@material-ui/icons";
import GetCardType from "../../utils/card-type";
import style from "../ChefProfile/style";
import {getPaymentInfo} from "../../redux/user";

const amex = "/images/amex.webp";
const discover = "/images/discover.webp";
const dinersclub = "/images/dinersclub.webp";
const jcb = "/images/jcb.webp";
const mastercard = "/images/mastercard.png";
const visa = "/images/visa.webp";
const unionpay = "/images/unionpay.png";

const addCardErrorLabel = 'Add card';

const AddCard = (props) => {
  const dispatch = useDispatch();

  const { isChef, userId } = useSelector(store => ({
    isChef: store.auth.isChef,
    userId: store.auth.userId,
  }));

  const initialState = {
    cardNumber: '',
    cardType: '',
    cvv: '',
    expiryMonth: '',
    expiryYear: '',
    nameOnCard: '',
  };

  const initialErrors = {
    cardNumber: 'A valid card number is required',
    isCardNumberErr: false,
    cvv: '3 digits',
    isCvvErr: false,
    expiryMonth: 'Month in 2 digits [01 - 12]',
    isExpiryMonthErr: false,
    expiryYear: 'Last 2 digits for year',
    isExpiryYearErr: false,
  };

  const [state, setState] = useReducer(initReducer, initialState);
  const [errors, setErrors] = useReducer(initReducer, initialErrors);

  useEffect(() => {
    setState(initialState);
    setErrors(initialErrors);
  }, []);

  const processOnlyNumbers = input => {
    const updated = input.replaceAll('-', '');
    if (isNaN(updated)) return false;

    return true;
  };

  const addCard = () => {
    const {
      isCardNumberErr,
      isCvvErr, isNameError,
      isExpiryMonthErr,
      isExpiryYearErr
    } = errors;
    try {
      if (isCardNumberErr || isCvvErr || isNameError || isExpiryYearErr || isExpiryMonthErr) {
        NotificationManager.error('Please resolve the errors.', addCardErrorLabel);
        return;
      }

      const {
        cardNumber, cvv,
        expiryMonth,
        expiryYear,
        nameOnCard
      } = state;

      const payload = {
        number: cardNumber,
        expMonth: expiryMonth,
        expYear: expiryYear,
        cvc: cvv
      };

      if (nameOnCard) payload.nameOnCard = nameOnCard;

      client.mutate({
        mutation: ADD_USER_PAYMENT,
        variables: {
          userId: userId,
          payment: {
            type: 'CARD',
            card: payload
          }
        }
      })
        .then(() => {
          NotificationManager.info('Card successfully added.', addCardErrorLabel);
          setState(initialState);
          setErrors(initialErrors);
          dispatch(getPaymentInfo(userId));
          props.close();
          props.onSuccess();
        })
        .catch(err => {
          console.error('ERR adding card (API) ', err);
          let errorObj = JSON.parse(JSON.stringify(err));
          if (errorObj.graphQLErrors) {
            errorObj.graphQLErrors.forEach(er => {
              NotificationManager.error(er.message, addCardErrorLabel);
            })
          } else {NotificationManager.error('There was an error processing your request. Try again.', addCardErrorLabel);}
          setState(initialState);
          setErrors(initialErrors);
        })
    } catch (e) {
      console.error('ERROR Adding card: ', e);
      NotificationManager.error('There was an error, please try again.', addCardErrorLabel, 3500);
      setState(initialState);
      setErrors(initialErrors);
    }
  };

  const processCardNumber = cardNum => {
    let updated = cardNum;

    // American Express Card
    if (state.cardType.toLowerCase().startsWith('am')) {
      if (cardNum.length == 5) {
        const cardNumArr = cardNum.split('');
        const temp = cardNumArr[4];
        cardNumArr[4] = '-';
        cardNumArr.push(temp);
        updated = cardNumArr.join('');
        setState({ cardNumber: updated });
      }

      if (cardNum.length == 12) {
        const cardNumArr = cardNum.split('');
        const temp = cardNumArr[11];
        cardNumArr[11] = '-';
        cardNumArr.push(temp);
        updated = cardNumArr.join('');
        setState({ cardNumber: updated });
      }
    }
    else { // other cards apart from AMEX
      if (cardNum.length == 5) {
        const cardNumArr = cardNum.split('');
        const temp = cardNumArr[4];
        cardNumArr[4] = '-';
        cardNumArr.push(temp);
        updated = cardNumArr.join('');
        setState({ cardNumber: updated });
      }

      if (cardNum.length == 10) {
        const cardNumArr = cardNum.split('');
        const temp = cardNumArr[9];
        cardNumArr[9] = '-';
        cardNumArr.push(temp);
        updated = cardNumArr.join('');
        setState({ cardNumber: updated });
      }

      if (cardNum.length == 15) {
        const cardNumArr = cardNum.split('');
        const temp = cardNumArr[14];
        cardNumArr[14] = '-';
        cardNumArr.push(temp);
        updated = cardNumArr.join('');
        setState({ cardNumber: updated });
      }
    }
  };

  const handleClose = () => {
    setState(initialState);
    setErrors(initialErrors);
  };

  return (
    <Modal
      open={props.show}
      onClose={() => {
        handleClose();
        props.close();
      }}
      data-test-id={"add-card-modal-component"}
    >
      <div className="add-card-wrapper">
        <div className="add-card-header-container">
          <div className="add-card-header">
            Add new card
          </div>
          <div className="add-card-close-wrapper">
            <IconButton onClick={() => {
              handleClose();
              props.close();
            }} data-test-id={"add-card-modal-close-btn"}>
              <Cancel style={{ fontSize: 32 }} />
            </IconButton>
          </div>
        </div>

        <div className="add-card-container">

          <div className="add-card-number">
            <label>Card Number</label>
            <div className="add-card-number-wrapper">
              <input
                type="text"
                maxLength={19}
                placeholder={"XXXX-XXXX-XXXX-XXXX"}
                data-test-id={"card-number-input"}
                onBlur={() => {
                  if (state.cardNumber.length !== 17 && state.cardType.toLowerCase().startsWith('am')) {
                    setErrors({ isCardNumberErr: true })
                  } else if (state.cardNumber.length !== 19 && !state.cardType.toLowerCase().startsWith('am')) {
                    setErrors({ isCardNumberErr: true })
                  } else setErrors({ isCardNumberErr: false })
                }}
                onChange={(evt) => {
                  const cardNum = evt.target.value;
                  if (!processOnlyNumbers(cardNum)) return;
                  if (cardNum) {
                    const getCardType = GetCardType(cardNum.replaceAll('-', ''));
                    setState({ cardNumber: cardNum, cardType: getCardType });
                    setErrors({ isCardNumberErr: false })
                    if (cardNum.length > 4) processCardNumber(cardNum);
                  } else {
                    setState({ cardNumber: cardNum, cardType: '' });
                  }
                }}
                onKeyDown={evt => {
                  const key = evt.key;
                  if (key === 'Backspace' || key === 'Delete') setState({ cardNumber: '', cvv: '', cardType: '' })
                }}
                value={state.cardNumber}
                className={errors.isCardNumberErr ? 'add-card-input-error' : ''}
              />
              <div>
                {state.cardType !== '' && <img
                  src={state.cardType.toLowerCase().startsWith('visa') ? visa : (
                    state.cardType.toLowerCase().startsWith('master') ? mastercard : (
                      state.cardType.toLowerCase().startsWith('american') || state.cardType.toLowerCase().startsWith('amex') ? amex : (
                        state.cardType.toLowerCase().startsWith('disco') ? discover : (
                          state.cardType.toLowerCase().startsWith('diner') ? dinersclub : (
                            state.cardType.toLowerCase().startsWith('jc') ? jcb : unionpay
                          )
                        )
                      )
                    )
                  )}
                  style={{
                    marginTop: 0,
                    minHeight: 20,
                    maxHeight: 60,
                    minWidth: 61,
                    maxWidth: 120
                  }}
                />}
              </div>
            </div>
            {errors.isCardNumberErr && <div className="add-card-error">{errors.cardNumber}</div>}
          </div>

          <div className="card-expiry-wrapper">
            <div className="add-card-expiry">
              <label>Valid Thru (MM/YY)</label>
              <div className="card-expiry-container">
                <input
                  type="text"
                  maxLength={2}
                  placeholder={"MM"}
                  data-test-id={"card-expiry-month-input"}
                  onBlur={() => {
                    if (isNaN(parseInt(state.expiryMonth)) || state.expiryMonth.length !== 2) {
                      setErrors({ isExpiryMonthErr: true })
                    } else setErrors({ isExpiryMonthErr: false })
                  }}
                  onChange={evt => {
                    const cardMonth = evt.target.value;
                    if (!processOnlyNumbers(cardMonth)) return;

                    setState({ expiryMonth: cardMonth, isExpiryMonthErr: false })
                  }}
                  value={state.expiryMonth}
                /> /
                <input
                  type="text"
                  maxLength={2}
                  placeholder={"YY"}
                  data-test-id={"card-expiry-year-input"}
                  onBlur={() => {
                    if (isNaN(parseInt(state.expiryYear)) || state.expiryYear.length !== 2) {
                      setErrors({ isExpiryYearErr: true })
                    } else setErrors({ isExpiryYearErr: false })
                  }}
                  onChange={evt => {
                    const cardYear = evt.target.value;
                    if (!processOnlyNumbers(cardYear)) return;

                    setState({ expiryYear: cardYear, isExpiryYearErr: false  })
                  }}
                  value={state.expiryYear}
                />
              </div>
              {errors.isExpiryMonthErr && <div className="add-card-error">{errors.expiryMonth}</div>}
              {errors.isExpiryYearErr && <div className="add-card-error">{errors.expiryYear}</div>}
            </div>

            <div className="add-card-cvv">
              <label>CVV Code</label>
              <input
                type="text"
                maxLength={state.cardType.toLowerCase().startsWith('am') ? 4 : 3}
                placeholder={state.cardType.toLowerCase().startsWith('am') ? "XXXX" :"XXX"}
                data-test-id={"card-cvv-input"}
                onBlur={() => {
                  if (isNaN(parseInt(state.cvv)) || (!state.cardType.toLowerCase().startsWith('am') && state.cvv.length !== 3)) {
                    setErrors({ isCvvErr: true, cvv: '3 Digits' })
                  } else if (isNaN(parseInt(state.cvv)) || (state.cardType.toLowerCase().startsWith('am') && state.cvv.length !== 4)) {
                    setErrors({ isCvvErr: true, cvv: '4 Digits' })
                  } else setErrors({ isCvvErr: false })
                }}
                onChange={evt => {
                  const cardCvv = evt.target.value;

                  if (!processOnlyNumbers(cardCvv)) return;

                  setState({ cvv: cardCvv, isCvvErr: false });
                }}
                value={state.cvv}
              />
              {errors.isCvvErr && <div className="add-card-error">{errors.cvv}</div>}
            </div>
          </div>

          <div className="add-card-name">
            <label>Name on Card</label>
            <input
              type="text"
              placeholder={"Jane Doe"}
              data-test-id={"card-name-input"}
              onChange={evt => {
                const cardName = evt.target.value;

                if (cardName) {
                  setState({ nameOnCard: cardName });
                }
              }}
              value={state.nameOnCard}
            />
          </div>

        </div>

        <div className="add-card-btn-container">
          <Button className="add-card-cancel-btn" onClick={() => {
            handleClose();
            props.close();
          }} data-test-id={"add-card-cancel-btn"}>
            Cancel
          </Button>
          <Button className="add-card-add-btn" onClick={addCard} data-test-id={"add-card-btn"}>
            Add
          </Button>
        </div>

      </div>
    </Modal>
  );
};

export default AddCard;
